import React, { forwardRef, Ref } from 'react';
import { maxBy } from 'lodash';
import { Container, Stack } from '@fiverr-private/layout_components';
import { I18n } from '@fiverr-private/i18n-react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Typography } from '@fiverr-private/typography';
import { Loader } from '@fiverr-private/feedback';
import { SellerLegalDocsStats, SentTemplateData } from '../../../api';
import { formatDateToShortWithLocale } from '../../../utils/date';
import { getAggregatedStatus, getAggregatedStatusFromInitialData } from './utils';
import { Status } from './Status';

export interface ListStatusProps {
    documents?: SentTemplateData[];
    initialData?: SellerLegalDocsStats;
    hideDate?: boolean;
}

const TRANSLATION_KEY = 'legal_documents_package.seller_templates.list_status';

// eslint-disable-next-line react/display-name
export const ListStatus = forwardRef(
    ({ documents, initialData, hideDate, ...props }: ListStatusProps, ref: Ref<HTMLElement>) => {
        const { locale } = getContext();

        if (!initialData && !documents)
            return (
                <Container height="20px" paddingX="4">
                    <Loader size="sm" />
                </Container>
            );

        const { status, label } = documents
            ? getAggregatedStatus(documents)
            : getAggregatedStatusFromInitialData(initialData);
        const lastUpdatedDocument = maxBy(documents || [], (document) => new Date(document.updatedAt).valueOf());

        return (
            <Stack ref={ref} {...props} height="20px" alignItems="center">
                <Status type={status}>
                    <Typography whiteSpace="nowrap">
                        <I18n k={TRANSLATION_KEY} params={{ count: label }} />
                    </Typography>
                </Status>
                {!hideDate && lastUpdatedDocument && lastUpdatedDocument?.updatedAt && (
                    <Container display={{ default: 'none', sm: 'inlineBlock' }}>
                        <Typography
                            fontWeight="normal"
                            color="grey_1100"
                            size={{
                                default: 'b_sm',
                                sm: 'b_md',
                            }}
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            overflow="hidden"
                        >
                            {formatDateToShortWithLocale(lastUpdatedDocument?.updatedAt, locale)}
                        </Typography>
                    </Container>
                )}
            </Stack>
        );
    }
);
